import React, { useState } from 'react';
import { Edit, Trash2, Check, X, Pencil, Loader2, Truck, Boxes } from 'lucide-react';
import { toast } from 'sonner';
import { productsAPI } from '../services/api';
import BrandLogo from './BrandLogo';
import MarginBadge from './MarginBadge';
import { productSeoScore, SeoGradeBadge } from './SubcategorySeoScore';
import CompatCoverageBadge from './CompatCoverageBadge';

const stockColor = (s) => (s === 0 ? 'text-red-600' : s <= 5 ? 'text-amber-600' : 'text-gray-900');

const QuickEdit = ({ product, onSaved, onCancel }) => {
  const [price, setPrice] = useState(product.price);
  const [stock, setStock] = useState(product.stock ?? 0);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const p = parseFloat(String(price).replace(',', '.'));
    const s = parseInt(stock, 10);
    if (Number.isNaN(p) || p < 0 || Number.isNaN(s) || s < 0) { toast.error('Prix ou stock invalide'); return; }
    setSaving(true);
    try {
      const updated = await productsAPI.quickEdit(product.id, { price: p, stock: s });
      toast.success(`${product.name} : ${p.toFixed(2)} € · stock ${s}`);
      onSaved(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    } finally { setSaving(false); }
  };
  return (
    <div className="grid grid-cols-2 gap-2 pt-2 border-t" data-testid={`product-card-quick-edit-${product.id}`}>
      <label className="text-xs text-gray-500">Prix TTC (€)
        <input type="number" step="0.01" min="0" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} data-testid={`product-card-price-input-${product.id}`} className="mt-1 w-full border rounded-md px-2 py-2 text-sm text-gray-900" />
      </label>
      <label className="text-xs text-gray-500">Stock entrepôt
        <input type="number" step="1" min="0" inputMode="numeric" value={stock} onChange={(e) => setStock(e.target.value)} data-testid={`product-card-stock-input-${product.id}`} className="mt-1 w-full border rounded-md px-2 py-2 text-sm text-gray-900" />
      </label>
      <button type="button" onClick={onCancel} disabled={saving} data-testid={`product-card-quick-cancel-${product.id}`} className="inline-flex items-center justify-center gap-1 border rounded-md py-2 text-sm text-gray-700"><X size={14} /> Annuler</button>
      <button type="button" onClick={save} disabled={saving} data-testid={`product-card-quick-save-${product.id}`} className="inline-flex items-center justify-center gap-1 rounded-md py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60">{saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Enregistrer</button>
    </div>
  );
};

const ProductCard = ({ product, vatRate, seoScore, selected, onToggleSelect, onEdit, onDelete, onSaved }) => {
  const [quick, setQuick] = useState(false);
  return (
    <article data-testid={`product-card-${product.id}`} className={`bg-white rounded-lg shadow-sm p-3 space-y-2 ${selected ? 'ring-2 ring-orange-400' : ''}`}>
      <div className="flex items-start gap-3">
        <input type="checkbox" checked={selected} onChange={onToggleSelect} data-testid={`product-card-select-${product.id}`} className="mt-1 rounded border-gray-300 text-orange-500 shrink-0" />
        <img src={product.image} alt="" className="w-14 h-14 object-contain shrink-0 bg-gray-50 rounded" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 leading-tight" data-testid={`product-card-name-${product.id}`}>{product.name}</p>
          <p className="text-xs text-gray-400 truncate">{product.category}{product.subcategory ? ` › ${product.subcategory}` : ''}</p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs text-gray-500">
            <BrandLogo brand={product.brand} size="xs" />
            <span className="font-mono">{product.ref}</span>
            {product.published === false && <span className="text-[10px] font-semibold uppercase bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">Non publié</span>}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-lg font-bold text-gray-900" data-testid={`product-card-price-${product.id}`}>{product.price.toFixed(2)} €</p>
          {product.packs?.length > 0 && <p className="text-[11px] text-orange-700 flex items-center gap-1"><Boxes size={11} /> {product.packs.length} pack{product.packs.length > 1 ? 's' : ''}</p>}
        </div>
        <div className="text-right text-xs" data-testid={`product-card-stock-${product.id}`}>
          <p><span className={`text-base font-semibold ${stockColor(product.stock ?? 0)}`}>{product.stock ?? 0}</span> <span className="text-gray-400">entrepôt</span></p>
          {product.supplier_stock > 0 && <p className="text-blue-700 flex items-center justify-end gap-1"><Truck size={11} /> +{product.supplier_stock} fournisseur</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <MarginBadge product={product} vatRate={vatRate} testId={`product-card-margin-${product.id}`} />
        <SeoGradeBadge score={seoScore} testId={`product-card-seo-${product.id}`} />
        <CompatCoverageBadge product={product} />
      </div>
      {quick ? (
        <QuickEdit product={product} onCancel={() => setQuick(false)} onSaved={(u) => { setQuick(false); onSaved(u); }} />
      ) : (
        <div className="grid grid-cols-3 gap-2 pt-2 border-t">
          <button type="button" onClick={() => setQuick(true)} data-testid={`product-card-quick-${product.id}`} className="inline-flex items-center justify-center gap-1 rounded-md py-2 text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100"><Pencil size={14} /> Prix / stock</button>
          <button type="button" onClick={onEdit} data-testid={`product-card-edit-${product.id}`} className="inline-flex items-center justify-center gap-1 border rounded-md py-2 text-sm text-blue-700"><Edit size={14} /> Fiche</button>
          <button type="button" onClick={onDelete} data-testid={`product-card-delete-${product.id}`} className="inline-flex items-center justify-center gap-1 border rounded-md py-2 text-sm text-red-600"><Trash2 size={14} /> Suppr.</button>
        </div>
      )}
    </article>
  );
};

const AdminProductCards = ({ products, vatRate, selectedIds, onToggleSelect, onEdit, onDelete, onSaved }) => (
  <div className="space-y-3" data-testid="products-cards">
    {products.map((p) => (
      <ProductCard key={p.id} product={p} vatRate={vatRate} seoScore={productSeoScore(p)} selected={selectedIds.includes(p.id)} onToggleSelect={() => onToggleSelect(p.id)} onEdit={() => onEdit(p)} onDelete={() => onDelete(p.id)} onSaved={onSaved} />
    ))}
  </div>
);

export default AdminProductCards;
